import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const ChecklistGenerator = ({ isOpen, onClose }) => {
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [selectedTasks, setSelectedTasks] = useState({});
  const [generatedChecklist, setGeneratedChecklist] = useState(null);
  const [checklistName, setChecklistName] = useState('');

  const roomOptions = [
    { id: 'kitchen', name: 'Kitchen', icon: 'ChefHat' },
    { id: 'living-room', name: 'Living Room', icon: 'Sofa' },
    { id: 'bedroom', name: 'Bedroom', icon: 'Bed' },
    { id: 'bathroom', name: 'Bathroom', icon: 'Bath' },
    { id: 'dining-room', name: 'Dining Room', icon: 'UtensilsCrossed' },
    { id: 'office', name: 'Home Office', icon: 'Monitor' },
    { id: 'laundry', name: 'Laundry Room', icon: 'Shirt' },
    { id: 'garage', name: 'Garage', icon: 'Car' }
  ];

  const tasksByRoom = {
    kitchen: [
      'Clean and disinfect countertops',
      'Clean appliances (microwave, toaster, coffee maker)',
      'Clean inside and outside of refrigerator',
      'Clean stovetop and oven',
      'Clean sink and faucet',
      'Wipe down cabinets and handles',
      'Sweep and mop floor',
      'Empty trash and replace liner',
      'Clean backsplash',
      'Organize pantry'
    ],
    'living-room': [
      'Dust all surfaces and furniture',
      'Vacuum upholstery',
      'Clean coffee table and side tables',
      'Dust electronics and entertainment center',
      'Vacuum or sweep floor',
      'Clean windows and mirrors',
      'Organize books and magazines',
      'Dust light fixtures',
      'Clean baseboards',
      'Fluff and arrange pillows'
    ],
    bedroom: [
      'Make beds and change linens',
      'Dust nightstands and dresser',
      'Vacuum or sweep floor',
      'Clean mirrors',
      'Organize closet',
      'Dust light fixtures',
      'Clean windows',
      'Vacuum under bed',
      'Wipe down surfaces',
      'Empty trash'
    ],
    bathroom: [
      'Clean and disinfect toilet',
      'Clean shower/bathtub',
      'Clean sink and faucet',
      'Clean mirror',
      'Sweep and mop floor',
      'Clean towel racks',
      'Restock supplies',
      'Empty trash',
      'Clean light fixtures',
      'Organize medicine cabinet'
    ],
    'dining-room': [
      'Dust dining table and chairs',
      'Clean light fixture/chandelier',
      'Vacuum or sweep floor',
      'Clean windows',
      'Dust china cabinet',
      'Polish wood furniture',
      'Clean baseboards',
      'Organize dining accessories',
      'Wipe down surfaces',
      'Clean mirrors'
    ],
    office: [
      'Dust desk and computer',
      'Organize papers and supplies',
      'Clean keyboard and mouse',
      'Dust monitor and electronics',
      'Vacuum floor',
      'Empty trash',
      'Clean windows',
      'Dust bookshelves',
      'Wipe down surfaces',
      'Organize cables'
    ],
    laundry: [
      'Clean washer and dryer',
      'Organize laundry supplies',
      'Sweep and mop floor',
      'Clean sink',
      'Dust shelving',
      'Empty lint trap',
      'Wipe down surfaces',
      'Organize cleaning supplies',
      'Clean light fixtures',
      'Empty trash'
    ],
    garage: [
      'Sweep floor',
      'Organize tools',
      'Clean workbench',
      'Dust shelving',
      'Organize storage boxes',
      'Clean windows',
      'Remove cobwebs',
      'Organize sports equipment',
      'Clean garage door',
      'Sort and dispose items'
    ]
  };

  const handleRoomSelection = (roomId, checked) => {
    if (checked) {
      setSelectedRooms(prev => [...prev, roomId]);
      setSelectedTasks(prev => ({
        ...prev,
        [roomId]: tasksByRoom?.[roomId] || []
      }));
    } else {
      setSelectedRooms(prev => prev?.filter(id => id !== roomId));
      setSelectedTasks(prev => {
        const newTasks = { ...prev };
        delete newTasks?.[roomId];
        return newTasks;
      });
    }
  };

  const handleTaskSelection = (roomId, task, checked) => {
    setSelectedTasks(prev => ({
      ...prev,
      [roomId]: checked
        ? [...(prev?.[roomId] || []), task]
        : (prev?.[roomId] || [])?.filter(t => t !== task)
    }));
  };

  const generateChecklist = () => {
    const checklist = {
      name: checklistName || `Cleaning Checklist - ${new Date()?.toLocaleDateString()}`,
      createdDate: new Date()?.toISOString(),
      rooms: selectedRooms?.map(roomId => ({
        id: roomId,
        name: roomOptions?.find(r => r?.id === roomId)?.name,
        tasks: selectedTasks?.[roomId] || []
      }))?.filter(room => room?.tasks?.length > 0),
      totalTasks: Object.values(selectedTasks)?.flat()?.length
    };

    setGeneratedChecklist(checklist);
  };

  const downloadPDF = () => {
    // In a real app, this would generate and download a PDF
    console.log('Downloading PDF checklist:', generatedChecklist);
    alert('PDF download feature would be implemented here');
  };

  const printChecklist = () => {
    window.print();
  };

  const shareChecklist = () => {
    if (navigator.share) {
      navigator.share({
        title: generatedChecklist?.name,
        text: `My cleaning checklist with ${generatedChecklist?.totalTasks} tasks`,
        url: window.location?.href
      });
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard?.writeText(window.location?.href);
      alert('Checklist link copied to clipboard!');
    }
  };

  const resetGenerator = () => {
    setSelectedRooms([]);
    setSelectedTasks({});
    setGeneratedChecklist(null);
    setChecklistName('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-elevation-5 w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="CheckSquare" size={24} color="var(--color-success)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-text-primary">
                Cleaning Checklist Generator
              </h2>
              <p className="text-sm text-text-secondary">
                Create customized cleaning checklists for your home
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close checklist generator"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {!generatedChecklist ? (
            /* Generator Interface */
            (<div className="p-6 max-h-96 overflow-y-auto">
              <div className="space-y-6">
                {/* Checklist Name */}
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Checklist Name (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Weekly Deep Clean, Move-in Checklist"
                    value={checklistName}
                    onChange={(e) => setChecklistName(e?.target?.value)}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>

                {/* Room Selection */}
                <div>
                  <h3 className="text-lg font-medium text-text-primary mb-4">
                    Select Rooms to Clean
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {roomOptions?.map((room) => (
                      <label
                        key={room?.id}
                        className={`flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                          selectedRooms?.includes(room?.id)
                            ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRooms?.includes(room?.id)}
                          onChange={(e) => handleRoomSelection(room?.id, e?.target?.checked)}
                          className="sr-only"
                        />
                        <Icon name={room?.icon} size={24} color={selectedRooms?.includes(room?.id) ? 'var(--color-primary)' : 'var(--color-text-secondary)'} />
                        <span className={`text-sm font-medium mt-2 ${
                          selectedRooms?.includes(room?.id) ? 'text-primary' : 'text-text-secondary'
                        }`}>
                          {room?.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Task Selection */}
                {selectedRooms?.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-text-primary mb-4">
                      Customize Tasks for Each Room
                    </h3>
                    <div className="space-y-6">
                      {selectedRooms?.map((roomId) => {
                        const room = roomOptions?.find(r => r?.id === roomId);
                        const roomTasks = tasksByRoom?.[roomId] || [];
                        
                        return (
                          <div key={roomId} className="bg-surface rounded-lg p-4">
                            <div className="flex items-center space-x-2 mb-3">
                              <Icon name={room?.icon} size={20} color="var(--color-primary)" />
                              <h4 className="font-medium text-text-primary">{room?.name}</h4>
                              <span className="text-sm text-text-secondary">
                                ({(selectedTasks?.[roomId] || [])?.length}/{roomTasks?.length} selected)
                              </span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {roomTasks?.map((task) => (
                                <label key={task} className="flex items-center space-x-2 p-2 rounded hover:bg-muted cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={(selectedTasks?.[roomId] || [])?.includes(task)}
                                    onChange={(e) => handleTaskSelection(roomId, task, e?.target?.checked)}
                                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                                  />
                                  <span className="text-sm text-text-primary">{task}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Generate Button */}
                {selectedRooms?.length > 0 && Object.values(selectedTasks)?.some(tasks => tasks?.length > 0) && (
                  <div className="flex justify-center pt-4">
                    <Button
                      variant="default"
                      onClick={generateChecklist}
                      iconName="CheckSquare"
                      iconPosition="left"
                      size="lg"
                    >
                      Generate Checklist
                    </Button>
                  </div>
                )}
              </div>
            </div>)
          ) : (
            /* Generated Checklist Display */
            (<div className="flex flex-col h-full">
              <div className="p-6 flex-1 overflow-y-auto">
                <div className="max-w-2xl mx-auto">
                  {/* Checklist Header */}
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-text-primary mb-2">
                      {generatedChecklist?.name}
                    </h3>
                    <p className="text-text-secondary">
                      Created on {new Date(generatedChecklist.createdDate)?.toLocaleDateString()}
                    </p>
                    <div className="flex justify-center space-x-4 mt-2 text-sm text-text-secondary">
                      <span>{generatedChecklist?.rooms?.length} rooms</span>
                      <span>•</span>
                      <span>{generatedChecklist?.totalTasks} tasks</span>
                    </div>
                  </div>

                  {/* Checklist Content */}
                  <div className="space-y-6" id="checklist-content">
                    {generatedChecklist?.rooms?.map((room) => (
                      <div key={room?.id} className="bg-surface rounded-lg p-4">
                        <h4 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
                          <Icon name={roomOptions?.find(r => r?.id === room?.id)?.icon} size={20} />
                          <span>{room?.name}</span>
                        </h4>
                        <div className="space-y-2">
                          {room?.tasks?.map((task, index) => (
                            <label key={index} className="flex items-center space-x-3 p-2 rounded hover:bg-muted cursor-pointer">
                              <input
                                type="checkbox"
                                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                              />
                              <span className="text-sm text-text-primary">{task}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              {/* Action Buttons */}
              <div className="border-t border-border p-6">
                <div className="flex flex-wrap justify-center gap-3">
                  <Button
                    variant="default"
                    onClick={downloadPDF}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download PDF
                  </Button>
                  <Button
                    variant="outline"
                    onClick={printChecklist}
                    iconName="Printer"
                    iconPosition="left"
                  >
                    Print
                  </Button>
                  <Button
                    variant="outline"
                    onClick={shareChecklist}
                    iconName="Share"
                    iconPosition="left"
                  >
                    Share
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={resetGenerator}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Create New
                  </Button>
                </div>
              </div>
            </div>)
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistGenerator;